<script setup lang="ts">
import { ref, watch } from "vue";
import { RouteLocationRaw } from "vue-router";

import {
  type IConsumptionOverviewSiteRow,
  useConsumptionOverviewSites,
} from "@/components/data/table/use-consumption-overview-sites";
import { useSearchFilter } from "@/composables/use-search-filter";
import { DATA_TEST_IDS } from "@/constants/data-test-ids/data-test-ids.constant";
import { V4HSiteId } from "@/data/common/ids";
import { useTypedIntl } from "@/locales/typed-intl";
import router from "@/router/router";

import Column from "primevue/column";
import DataTable from "primevue/datatable";
import IconField from "primevue/iconfield";
import InputIcon from "primevue/inputicon";
import InputText from "primevue/inputtext";

import ColoredBadge from "@/components/ui/colored-badge.vue";

interface IProps {
  autoSelectSingleSite?: boolean;
  searchInputDataTestId?: string;
  routerLinkTo: (siteId: V4HSiteId) => RouteLocationRaw;
}

interface IEmits {
  (e: "search-used"): void;
}

const props = withDefaults(defineProps<IProps>(), {
  autoSelectSingleSite: false,
  searchInputDataTestId: DATA_TEST_IDS.components.consumptionOverviewSites.searchSiteInput,
});
const emits = defineEmits<IEmits>();

const { t, k } = useTypedIntl();
const { isLoading, mainConsumptionSource, rows, sites } = useConsumptionOverviewSites();
const hasAutoSelectedSingleSite = ref(false);

watch(
  sites,
  async (newSites) => {
    if (props.autoSelectSingleSite && !hasAutoSelectedSingleSite.value && newSites?.length === 1) {
      hasAutoSelectedSingleSite.value = true;
      await router.push(props.routerLinkTo(newSites[0].siteId));
    }
  },
  { immediate: true },
);

const { searchInput: searchSiteInput, filteredList: filteredSites } = useSearchFilter<IConsumptionOverviewSiteRow>(
  rows,
  "name",
);

watch(searchSiteInput, () => emits("search-used"), { once: true });
</script>

<template>
  <div class="flex gap-4 mb-4 w-full">
    <IconField class="w-full">
      <InputIcon class="pi pi-search" />
      <InputText
        v-model.trim="searchSiteInput"
        :data-test-id="props.searchInputDataTestId"
        :placeholder="t(k.global.searchSite)"
      />
    </IconField>
  </div>

  <div class="w-full h-full mb-8">
    <div class="relative w-full h-full bg-white rounded-xl">
      <div class="absolute inset-4">
        <DataTable
          class="consumption-overview-sites-data-table"
          :data-test-id="DATA_TEST_IDS.components.consumptionOverviewSites.sitesDataTable.table"
          :loading="isLoading"
          removable-sort
          scroll-height="flex"
          scrollable
          selection-mode="single"
          :value="filteredSites"
        >
          <Column
            class="max-w-[400px]"
            field="name"
            :header="t(k.data.consumptionMonitoring.sitesDataTable.header.siteName)"
            sortable
          >
            <template #body="row">
              <RouterLink
                class="pl-4"
                :to="props.routerLinkTo(row.data.id)"
              >
                <div
                  class="w-full flex items-center gap-2"
                  :data-test-id="DATA_TEST_IDS.components.consumptionOverviewSites.sitesDataTable.siteName"
                >
                  <ColoredBadge :name="row.data.name" />
                  <h5 class="gap-2 items-center whitespace-nowrap text-ellipsis overflow-hidden">
                    {{ row.data.name }}
                  </h5>
                </div>
              </RouterLink>
            </template>
          </Column>
          <Column
            field="sectionCount"
            sortable
          >
            <template #header>
              <div class="flex flex-col">
                <h6>{{ t(k.data.consumptionMonitoring.sitesDataTable.header.sections) }}</h6>
                <small>{{ t(k.data.consumptionMonitoring.sitesDataTable.header.subtitle.bySite) }}</small>
              </div>
            </template>
            <template #body="row">
              <RouterLink :to="props.routerLinkTo(row.data.id)">
                {{ row.data.sectionCount ?? "-" }}
              </RouterLink>
            </template>
          </Column>
          <Column
            field="applianceCount"
            sortable
          >
            <template #header>
              <div class="flex flex-col">
                <h6>{{ t(k.data.consumptionMonitoring.sitesDataTable.header.appliances) }}</h6>
                <small>{{ t(k.data.consumptionMonitoring.sitesDataTable.header.subtitle.bySite) }}</small>
              </div>
            </template>
            <template #body="row">
              <RouterLink :to="props.routerLinkTo(row.data.id)">
                {{ row.data.applianceCount ?? "-" }}
              </RouterLink>
            </template>
          </Column>
          <Column
            field="consumptionValue"
            sortable
          >
            <template #header>
              <div class="flex flex-col">
                <h6>
                  {{ t(k.data.consumptionMonitoring.sitesDataTable.header.consumption[mainConsumptionSource]) }}
                </h6>
                <small>{{ t(k.data.consumptionMonitoring.sitesDataTable.header.subtitle.last30days) }}</small>
              </div>
            </template>
            <template #body="row">
              <RouterLink :to="props.routerLinkTo(row.data.id)">
                {{ row.data.consumptionLabel }}
              </RouterLink>
            </template>
          </Column>
          <Column>
            <template #body="row">
              <RouterLink
                class="pr-4"
                :to="props.routerLinkTo(row.data.id)"
              >
                <div class="flex justify-end mr-8">
                  <i class="pi pi-angle-right text-text-secondary" />
                </div>
              </RouterLink>
            </template>
          </Column>
        </DataTable>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.consumption-overview-sites-data-table {
  .p-datatable-table-container {
    @apply border-none rounded-none !important;

    table {
      thead {
        @apply shadow-none;

        th {
          @apply border-none;
        }
      }

      tbody {
        tr {
          &:hover {
            @apply bg-transparent;

            td {
              @apply border-blue-300;
            }
          }

          td {
            @apply p-0;
            @apply h-[80px] border-transparent;
            @apply border-y-2 !important;

            a {
              @apply flex w-full h-full items-center;
            }

            &:first-child {
              @apply rounded-l-lg border-l-2;
            }

            &:last-child {
              @apply rounded-r-lg border-r-2 border-b-2;
            }
          }
        }
      }
    }
  }
}
</style>
